import { Subscription, SubscriptionPlan } from "../../../interfaces/SubscriptionObject";
import { coerceSingle } from "../../helpers/ApiEnvelopeHelper";

// Subscriptions API is already camelCase (new convention) — this converter
// only null-guards so a partial backend response can never crash the UI.

export function convertSubscription(raw: Record<string, unknown>): Subscription {
  return {
    businessId: String(raw?.businessId ?? ""),
    subscriptionId: raw?.subscriptionId ? String(raw.subscriptionId) : undefined,
    planName: String(raw?.planName ?? ""),
    subscriptionPrice: String(raw?.subscriptionPrice ?? ""),
    billingPeriod: String(raw?.billingPeriod ?? ""),
    state: String(raw?.state ?? ""),
    headline: String(raw?.headline ?? ""),
    expiryDate: raw?.expiryDate ? String(raw.expiryDate) : null,
    subscriptionSessionId: raw?.subscriptionSessionId ? String(raw.subscriptionSessionId) : null,
    subscriptionEnd: raw?.subscriptionEnd ? String(raw.subscriptionEnd) : null,
    cardLabel: raw?.cardLabel ? String(raw.cardLabel) : null,
  };
}

/** contents of business/{businessId}/subscribe or subscriptions/cancel →
 *  a single Subscription (same envelope shape for both). */
export function convertSubscriptionEnvelope(contents: unknown): Subscription {
  return convertSubscription(coerceSingle(contents));
}

/**
 * contents of subscriptions/user → Subscription[]. Shape-tolerant (same
 * ambiguity as convertPendingBusinessList — see ApiEnvelopeHelper header):
 *  1. { subscriptions: [...] }        — doc shape
 *  2. [ { subscriptions: [...] } ]    — array-wrapped wrapper (matches the
 *                                        sample response in the API doc)
 *  3. [ {businessId, ...}, ... ]      — raw rows directly in contents
 * Detection checks for shape 2's `.subscriptions` array on the first element
 * FIRST, then falls back to treating contents as raw rows (shape 3) — rather
 * than the reverse (probing contents[0].businessId to detect shape 3). That
 * ordering matters: a malformed shape-3 row missing businessId must not be
 * misread as shape 2 and cause coerceSingle to swallow the whole list down to
 * `[]`. convertSubscription null-guards every field, so a malformed row just
 * yields a mostly-empty Subscription instead of dropping its siblings.
 */
export function convertUserSubscriptionsList(contents: unknown): Subscription[] {
  if (Array.isArray(contents)) {
    const first = contents[0] as Record<string, unknown> | undefined;
    if (first && Array.isArray(first.subscriptions)) {
      // Shape 2: a single wrapper element carrying `.subscriptions`.
      return (first.subscriptions as unknown[]).map((row) =>
        convertSubscription(row as Record<string, unknown>)
      );
    }
    // Shape 3 (or an empty array): contents IS the row array.
    return contents.map((row) => convertSubscription(row as Record<string, unknown>));
  }
  // Shape 1: a direct wrapper object.
  const wrapper = coerceSingle(contents);
  const rows = wrapper.subscriptions;
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => convertSubscription(row as Record<string, unknown>));
}

/** One row of the subscription plans catalog. Null-guarded like
 *  convertSubscription — an unexpected/partial row degrades to a mostly-empty
 *  SubscriptionPlan rather than crashing the UI. */
export function convertSubscriptionPlan(raw: Record<string, unknown>): SubscriptionPlan {
  return {
    subscriptionId: String(raw?.subscriptionId ?? ""),
    planCode: String(raw?.planCode ?? ""),
    planName: String(raw?.planName ?? ""),
    amountPaise: typeof raw?.amountPaise === "number" ? raw.amountPaise : null,
    currency: String(raw?.currency ?? ""),
    billingInterval: typeof raw?.billingInterval === "number" ? raw.billingInterval : 1,
    billingPeriod: String(raw?.billingPeriod ?? ""),
    active: raw?.active === true,
  };
}

/**
 * contents of subscriptions/plans → SubscriptionPlan[]. Unlike
 * convertUserSubscriptionsList, the observed sample has `contents` as a flat
 * array of plan rows directly (no wrapper key) — so no shape-detection is
 * needed here. If the backend later wraps this, this function will need the
 * same shape-tolerance treatment.
 */
export function convertSubscriptionPlansList(contents: unknown): SubscriptionPlan[] {
  if (!Array.isArray(contents)) return [];
  return contents.map((row) => convertSubscriptionPlan(row as Record<string, unknown>));
}
